import {
  ApproveEnrollmentDialog,
  CancelEnrollmentDialog,
  GcpBox,
  GcpTypography,
  RejectEnrollmentDialog,
} from '@back-office/ui';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Alert from '@material-ui/lab/Alert';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import BillerEnrollmentBreadcrumbs from '../biller-enrollment-detail/BillerEnrollmentBreadcrumbs';
import EnrollmentDetails from '../biller-enrollment-detail/EnrollmentDetails';
import RecurringAction from '../biller-enrollment-detail/RecurringAction';
import RecurringDetail from '../biller-enrollment-detail/RecurringPaymentDetail';
import UserDetail from '../biller-enrollment-detail/UserDetail';
import useViewModel from './viewmodel';

interface RecurringPaymentDetailProps {
  recurringId: string | null;
  handleOnClickBreadcrumbs: () => void;
  query: string;
}

export const useStyles = makeStyles(() => ({
  alert: {
    justifyContent: 'center',
    margin: '16px 0',
  },
}));

const RECURRING_EDIT = 'recurring.request.edit';

const RecurringPaymentDetail: FC<RecurringPaymentDetailProps> = observer(
  (props) => {
    const { recurringId, handleOnClickBreadcrumbs } = props;
    const classes = useStyles();
    const {
      recurringDetail,
      isLoading,
      approveDialog,
      toggleApproveDialog,
      rejectDialog,
      toggleRejectDialog,
      cancelDialog,
      toggleCancelDialog,
      alertMessage,
      alertStatus,
      isRecurringPending,
      isRecurringApproved,
      rejectReasonTypes,
      cancelReasonTypes,
      onClickReject,
      onClickCancel,
      handleCancelRecurring,
      handleApproveRecurring,
      handleRejectRecurring,
      sortAndFilterRejectReasonType,
      auth,
    } = useViewModel({ recurringId });

    return !isLoading ? (
      <GcpBox mb={4}>
        <BillerEnrollmentBreadcrumbs
          handleOnClickBreadcrumbs={handleOnClickBreadcrumbs}
        />
        {(auth.currentUser.role === 'ADMIN' ||
          auth.currentUser.permissions?.includes(RECURRING_EDIT)) && (
          <RecurringAction
            fullName={recurringDetail.full_name}
            handleOnClickBreadcrumbs={handleOnClickBreadcrumbs}
            showApproveBtn={isRecurringPending}
            showCancelBtn={isRecurringApproved}
            shownRejectBtn={isRecurringPending}
            onApprove={() => toggleApproveDialog(true)}
            onCancel={onClickCancel}
            onReject={onClickReject}
          />
        )}

        {alertMessage && (
          <Alert
            className={classes.alert}
            icon={false}
            severity={alertStatus === 'Approved' ? 'success' : 'error'}
          >
            <GcpTypography variant="body1">{alertMessage}</GcpTypography>
          </Alert>
        )}

        <UserDetail
          createdBy={recurringDetail.created_by}
          fullName={recurringDetail.full_name}
          mobileNo={recurringDetail.mobile_no}
          walletId={recurringDetail.wallet_id}
        />
        <EnrollmentDetails
          createdAt={recurringDetail.created_at}
          enrollmentId={recurringDetail.enrollment_id}
          reason={recurringDetail.reason}
          status={recurringDetail.status}
        />
        <RecurringDetail
          billerAliasName={recurringDetail.biller_alias_name}
          frequency={recurringDetail.frequency}
          merchantName={recurringDetail.merchant_name}
          referenceNumber={recurringDetail.reference_number}
          remindMeFlag={recurringDetail.remind_me_flag}
          sof={recurringDetail.sof}
          updatedAt={recurringDetail.updated_at}
          updatedBy={recurringDetail.updated_by}
        />
        <ApproveEnrollmentDialog
          createdAt={recurringDetail.created_at}
          merchantName={recurringDetail.merchant_name}
          open={approveDialog}
          referenceNumber={recurringDetail.reference_number}
          onCancel={() => toggleApproveDialog(false)}
          onSubmit={handleApproveRecurring}
        />

        <RejectEnrollmentDialog
          createdAt={recurringDetail.created_at}
          merchantName={recurringDetail.merchant_name}
          open={rejectDialog}
          referenceNumber={recurringDetail.reference_number}
          rejectTypes={sortAndFilterRejectReasonType(rejectReasonTypes.list)}
          onCancel={() => toggleRejectDialog(false)}
          onSubmit={handleRejectRecurring}
        />

        <CancelEnrollmentDialog
          cancelTypes={sortAndFilterRejectReasonType(cancelReasonTypes.list)}
          createdAt={recurringDetail.created_at}
          merchantName={recurringDetail.merchant_name}
          open={cancelDialog}
          referenceNumber={recurringDetail.reference_number}
          onCancel={() => toggleCancelDialog(false)}
          onSubmit={handleCancelRecurring}
        />
      </GcpBox>
    ) : null;
  }
);

export default RecurringPaymentDetail;
